import { useQuery } from '@tanstack/react-query';
import type {
  LocationDeliveriesQuery,
  LocationDeliveriesResponse,
} from '@citibox-panel/api-client';

export interface UseLocationDeliveries {
  data: LocationDeliveriesResponse | undefined;
  isPending: boolean;
  isError: boolean;
}

// `id` is the INTEGER location id the endpoint knows, never the shell's namespaced `loc:<id>` tab
// key — the caller unwraps it. `filters` is the read's own query and nothing more: the tab's
// display-only carrier label is dropped before it gets here, so it can neither reach a request nor
// split the cache. The endpoint does all the filtering; nothing here narrows its answer.
export function useLocationDeliveries(
  id: number,
  filters: LocationDeliveriesQuery,
  fetchLocationDeliveries: (
    id: number,
    query: LocationDeliveriesQuery,
  ) => Promise<LocationDeliveriesResponse>,
): UseLocationDeliveries {
  const { data, isPending, isError } = useQuery({
    // `filters` is part of the key, so editing one adds an entry instead of overwriting the
    // previous range's rows.
    queryKey: ['location-deliveries', id, filters],
    queryFn: () => fetchLocationDeliveries(id, filters),
  });

  // No `refetch`: the failed read is reported by the floating notice, which carries no action
  // (ADR 0021 §fourth amendment), and nothing is left in the list either — so the read reloads by
  // remounting. Handing one back would be re-adding the retry through the hook.
  return { data, isPending, isError };
}
